// HTML5 canvas signature pad. Supports mouse + touch + Apple Pencil.
// Exposes onChange(base64PngOrNull) on every stroke. ref.clear() / ref.toPng().

import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';

const SignaturePad = forwardRef(function SignaturePad({ onChange, height = 160 }, ref) {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [hasInk, setHasInk] = useState(false);
  const lastRef = useRef({ x: 0, y: 0 });

  function ctx() { return canvasRef.current?.getContext('2d'); }

  function resize() {
    const c = canvasRef.current; if (!c) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = c.getBoundingClientRect();
    c.width  = rect.width  * dpr;
    c.height = rect.height * dpr;
    const g = c.getContext('2d');
    g.scale(dpr, dpr);
    g.lineCap = 'round'; g.lineJoin = 'round';
    g.lineWidth = 2.2; g.strokeStyle = '#0f172a';
  }

  useEffect(() => {
    resize();
    const onR = () => { const had = hasInk; resize(); if (had) clear(); };
    window.addEventListener('resize', onR);
    return () => window.removeEventListener('resize', onR);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function pos(e) {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const t = e.touches?.[0] || e;
    return { x: t.clientX - rect.left, y: t.clientY - rect.top };
  }

  function start(e) { e.preventDefault(); setDrawing(true); lastRef.current = pos(e); }
  function move(e) {
    if (!drawing) return;
    e.preventDefault();
    const p = pos(e); const g = ctx();
    g.beginPath(); g.moveTo(lastRef.current.x, lastRef.current.y); g.lineTo(p.x, p.y); g.stroke();
    lastRef.current = p;
    if (!hasInk) { setHasInk(true); onChange?.(canvasRef.current.toDataURL('image/png')); }
  }
  function end() {
    if (!drawing) return;
    setDrawing(false);
    if (hasInk) onChange?.(canvasRef.current.toDataURL('image/png'));
  }

  function clear() {
    const c = canvasRef.current; if (!c) return;
    const g = c.getContext('2d');
    g.setTransform(1,0,0,1,0,0);
    g.clearRect(0,0,c.width,c.height);
    resize();
    setHasInk(false);
    onChange?.(null);
  }

  useImperativeHandle(ref, () => ({
    clear,
    toPng: () => hasInk ? canvasRef.current.toDataURL('image/png') : null,
    isEmpty: () => !hasInk,
  }), [hasInk]);

  return (
    <div className="relative">
      <canvas
        ref={canvasRef}
        style={{ height, touchAction: 'none' }}
        className="w-full bg-white border border-slate-300 rounded-md"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
      />
      <div className="flex items-center justify-between mt-1.5">
        <div className="text-xs text-slate-500">{hasInk ? 'Signed' : 'Sign with finger or stylus inside the box'}</div>
        <button type="button" onClick={clear} className="text-xs text-slate-500 hover:underline">Clear</button>
      </div>
    </div>
  );
});

export default SignaturePad;
